import type { InvoiceDto, TenantResponseDto } from '@saaga/billingv2-sdk'

import type { BillingTenant } from '../interfaces/billing.interface'

const MICROS_PER_DOLLAR = 1_000_000

export const formatCurrency = (amount: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)

export const formatBillingDate = (date: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)

export type BillingInterval = 'monthly' | 'yearly'

export const formatBillingPrice = (price: number, interval: BillingInterval): string => {
  if (price === 0) return 'Free'
  return `$${price}/${interval === 'monthly' ? 'mo' : 'yr'}`
}

export const microsToDollars = (micros: string | number): number => {
  const microsValue = typeof micros === 'string' ? Number.parseFloat(micros) : micros

  if (Number.isNaN(microsValue)) {
    return 0
  }

  return microsValue / MICROS_PER_DOLLAR
}

const toIsoString = (value: Date | string | null | undefined): string | null => {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  return new Date(value).toISOString()
}

export const transformTenantResponse = (sdkResponse: TenantResponseDto): BillingTenant => ({
  id: sdkResponse.id,
  name: sdkResponse.name ?? '',
  email: sdkResponse.email,
  address: sdkResponse.address
    ? {
        line1: sdkResponse.address.line1,
        line2: sdkResponse.address.line2,
        city: sdkResponse.address.city,
        state: sdkResponse.address.state,
        postalCode: sdkResponse.address.postalCode,
        country: sdkResponse.address.country,
      }
    : null,
  phone: sdkResponse.phone,
  taxId: sdkResponse.taxId,
  company: sdkResponse.company ?? undefined,
  subscriptionStatus: sdkResponse.subscription.status,
  subscriptionName: sdkResponse.subscription.name,
  subscriptionCancelAt: toIsoString(sdkResponse.subscription.cancelAt),
  subscriptionRenewalAt: toIsoString(sdkResponse.subscription.renewalDate),
  subscriptionsEnabled: sdkResponse.subscriptionsEnabled,
  balance: sdkResponse.balance,
  monthlyUsage: sdkResponse.monthlyUsage,
  defaultPaymentMethod: sdkResponse.defaultPaymentMethod
    ? {
        id: sdkResponse.defaultPaymentMethod.id,
        type: sdkResponse.defaultPaymentMethod.type,
        cardBrand: sdkResponse.defaultPaymentMethod.cardBrand,
        cardLast4: sdkResponse.defaultPaymentMethod.cardLast4,
        cardExpMonth: sdkResponse.defaultPaymentMethod.cardExpMonth,
        cardExpYear: sdkResponse.defaultPaymentMethod.cardExpYear,
      }
    : null,
  recentInvoiceList: sdkResponse.recentInvoices.map((invoice) => ({
    id: invoice.id,
    number: invoice.number,
    status: invoice.status,
    amountDue: invoice.amountDue / MICROS_PER_DOLLAR,
    amountPaid: invoice.amountPaid / MICROS_PER_DOLLAR,
    currency: invoice.currency,
    createdAt: new Date(invoice.created * 1000).toISOString(),
    hostedInvoiceUrl: invoice.hostedInvoiceUrl,
  })),
  createdAt: toIsoString(sdkResponse.createdAt) ?? new Date().toISOString(),
})

export type TransformedInvoice = {
  id: string
  number: string | null
  description: string | null
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible'
  amountDue: number
  amountPaid: number
  currency: string
  createdAt: Date
  dueDate: Date | null
  hostedInvoiceUrl: string | null
  downloadUrl: string | null
}

const parseInvoiceStatus = (
  status: string
): 'draft' | 'open' | 'paid' | 'void' | 'uncollectible' => {
  const validStatusList = ['draft', 'open', 'paid', 'void', 'uncollectible'] as const
  const normalizedStatus = status.toLowerCase()
  return validStatusList.includes(normalizedStatus as (typeof validStatusList)[number])
    ? (normalizedStatus as (typeof validStatusList)[number])
    : 'open'
}

const buildInvoiceDescription = (lineList: InvoiceDto['lines']): string | null => {
  if (!lineList || lineList.length === 0) return null
  return lineList[0].description || null
}

export const transformInvoiceResponse = (sdkInvoice: InvoiceDto): TransformedInvoice => ({
  id: sdkInvoice.id,
  number: sdkInvoice.number,
  description: buildInvoiceDescription(sdkInvoice.lines),
  status: parseInvoiceStatus(sdkInvoice.status),
  amountDue: sdkInvoice.amountDue / MICROS_PER_DOLLAR,
  amountPaid: sdkInvoice.amountPaid / MICROS_PER_DOLLAR,
  currency: sdkInvoice.currency.toUpperCase(),
  createdAt: new Date(sdkInvoice.created * 1000),
  dueDate: sdkInvoice.dueDate ? new Date(sdkInvoice.dueDate * 1000) : null,
  hostedInvoiceUrl: sdkInvoice.hostedInvoiceUrl,
  downloadUrl: sdkInvoice.invoicePdf,
})

export const transformInvoiceListResponse = (sdkInvoiceList: InvoiceDto[]): TransformedInvoice[] =>
  sdkInvoiceList.map(transformInvoiceResponse)

export const BillingErrorCode = {
  noAuthToken: 'BILLING_NO_AUTH_TOKEN',
  noOrganization: 'BILLING_NO_ORGANIZATION',
  tenantNotFound: 'BILLING_TENANT_NOT_FOUND',
  networkError: 'BILLING_NETWORK_ERROR',
  unknownError: 'BILLING_UNKNOWN_ERROR',
} as const

export type BillingErrorCode = (typeof BillingErrorCode)[keyof typeof BillingErrorCode]

export interface BillingError {
  code: BillingErrorCode
  message: string
  originalError?: unknown
}

export const createBillingError = (
  code: BillingErrorCode,
  message: string,
  originalError?: unknown
): BillingError => ({
  code,
  message,
  originalError,
})

export const billingErrorMessages: Record<BillingErrorCode, string> = {
  [BillingErrorCode.noAuthToken]: 'Please sign in to access billing',
  [BillingErrorCode.noOrganization]: 'Please select an organization',
  [BillingErrorCode.tenantNotFound]: 'Billing not configured for this organization',
  [BillingErrorCode.networkError]: 'Unable to connect to billing service',
  [BillingErrorCode.unknownError]: 'An unexpected billing error occurred',
}

export const getBillingErrorMessage = (error: BillingError): string =>
  billingErrorMessages[error.code] ?? error.message
